import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Notebook,
  GlossaryButton,
  EndSimulation,
} from "@learningblocks/storybook-components";
import {Utilities} from '@learningblocks/exp-constants';
const {resetScroll} = Utilities;
import Modal from "react-modal";

const NONE = "NONE";

Modal.setAppElement("#container");

export function CommonSectionContent(props) {
  useEffect(() => {
    resetScroll("book_content_body");
  }, [props.currentMenu]);

  const [endSimModal, setEndSimModal] = useState(false);

  const onClickNextSection = () => props.goToSection(props.nextMenuValue);

  const openModal = () => {
    setEndSimModal(true);
  }

  const closeModal = () => {
    setEndSimModal(false);
  }

  const isLastChapter = props.nextMenuValue === undefined;

  const footerContent = () => {
    if (isLastChapter) {
      return (
        <>
          <Notebook.EndSimulationBanner className="book_content_footer">
            <Notebook.EndSimulationBannerText
              className="book_content_footer--text"
              text={props.bannerText}
            />
            <Notebook.EndSimulationButtons className="book_content_footer--btns">
              <Notebook.DownloadReportButton
                className="download-btn"
                onClick={openModal}
              />
            </Notebook.EndSimulationButtons>
          </Notebook.EndSimulationBanner>
          <Modal isOpen={endSimModal} onRequestClose={closeModal} contentLabel="End simulation modal" 
            className="Modal"
            overlayClassName="Overlay"
          >
            <EndSimulation closeModal={closeModal}/>
          </Modal>
        </>
      );
    } else {
      return (
        <Notebook.ContentFooter className="book_content_footer">
          <Notebook.NextSectionButton
            className="next-button"
            onClick={onClickNextSection}
          />
        </Notebook.ContentFooter>
      );
    }
  };

  const contentBodyClass = isLastChapter
    ? "book_content_body lastChapter"
    : "book_content_body";

  return (
    <div 
      aria-hidden={props.collapsed}
      style={{
        display: "flex",
        flexDirection: "row-reverse",
      }}
    >
      <Notebook.IndexBlock className="book_index">
        <Notebook.Header className="book_index_header">
          <Notebook.TitleBlock
            className="index-titleBlock"
            title={props.title}
            text={props.description}
            ariaLabel="Lab notebook section heading."
          />
          <Notebook.DrawerIcon
            className="index-icon"
            collapsed={false}
            toggleCollapse={props.setNotebookVisible}
          />
          <GlossaryButton className="index-glossaryIcon" />
        </Notebook.Header>
        <Notebook.MenuList
          className="book_index_chapters"
          menuList={props.menuList}
          onSelect={props.goToSection}
          activeMenuClass="chapter-active"
          inactiveMenuClass="chapter"
        />
      </Notebook.IndexBlock>
      <Notebook.ContentBlock className="book_content">
        <Notebook.ContentBody className={contentBodyClass}>
          <Notebook.ContentReadArea className="read_block">
            <Notebook.ContentTitle
              className="content_title"
              title={props.currentMenu.title}
              number={props.currentMenu.number}
            />
            <Notebook.ContentComponents
              components={props.components}
              className="content_component"
            />
          </Notebook.ContentReadArea>
          {footerContent()}
        </Notebook.ContentBody>
      </Notebook.ContentBlock>
    </div>
  );
}

CommonSectionContent.propTypes = {
  /**
   * Components in current section to be displayed in Notebook
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * Details of currently active menu in menu list
   */
  currentMenu: PropTypes.shape({
    /**
     * Index of menu
     */
    number: PropTypes.number,
    /**
     * Title of menu
     */
    title: PropTypes.string,
    /**
     * Value of menu
     */
    value: PropTypes.string,
  }),
  /**
   * Description to be displayed in Notebook Header
   */
  description: PropTypes.string,
  /**
   * Function to go to next section
   */
  goToSection: PropTypes.func,
  /**
   * List of Menu Items
   */
  menuList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Title to display in  menu
       */
      title: PropTypes.string.isRequired,
      /**
       * Boolean specifying if menu is selected
       */
      current: PropTypes.bool.isRequired,
      /**
       * Index number of menu
       */
      number: PropTypes.number.isRequired,
      /**
       * Unique string value of the menu
       */
      value: PropTypes.string.isRequired,
      /**
       * Boolean set when section is visited
       */
      visited: PropTypes.bool.isRequired,
    })
  ),
  /**
   * Value for next-in-line menu
   * NONE value mens no more next section
   */
  nextMenuValue: PropTypes.oneOf([
    "CONTEXT",
    "MATERIALS",
    "PREDICTIONS",
    "TECHNIQUES",
    "DESIGN",
    "PROTOCOL",
    "PROBLEM_SOLVING",
    "RESULTS",
    "REFLECTION",
    "TROUBLESHOOTING",
    "SUMMARY",
    "NONE",
  ]),
  /**
   * Function to set new isNotebookVisible value
   */
  setNotebookVisible: PropTypes.func,
  /**
   * Title of the Content to display at header
   */
  title: PropTypes.string,
};

CommonSectionContent.defaultProps = {
  bannerText: "You have completed this simulation.",
};
